import type { ContactResearchResult as Result } from "../../lib/types";
import { BreakdownGrid } from "../BreakdownGrid";

interface Props {
  data: Result;
}

export function ContactResearchResult({ data }: Props) {
  const rows: Array<[string, string | number]> = [
    ["Apollo pulled", data.apollo_pulled ?? 0],
    ["Qualified (≥ threshold)", data.score_qualified ?? 0],
    ["Rejected", data.score_rejected ?? 0],
    ["Pipedrive/Lemlist excl.", data.pipedrive_excluded ?? 0],
    ["Crawl failed", data.crawl_failed ?? 0],
    ["Lemlist pushed", data.lemlist_pushed ?? 0],
    ["Tokens", (data.total_tokens ?? 0).toLocaleString("en-US")],
    ["Est. cost", `$${(data.estimated_cost_usd ?? 0).toFixed(4)}`],
  ];
  const contacts = Array.isArray(data.top_contacts) ? data.top_contacts : [];
  const warnings = Array.isArray(data.personalization_warnings)
    ? data.personalization_warnings
    : [];

  return (
    <div className="result">
      <h3>Summary</h3>
      <div className="stats">
        {rows.map(([label, value]) => (
          <div className="stat" key={label}>
            <span className="label">{label}</span>
            <span className="value">{value}</span>
          </div>
        ))}
      </div>

      {warnings.length > 0 && (
        <>
          <h3>Personalization warnings ({warnings.length})</h3>
          <p className="empty">
            These contacts were pushed to Lemlist without a personalizedLine —
            patch them manually before the sequence sends.
          </p>
          {warnings.map((w, i) => (
            <div className="contact" key={`warn-${w.email ?? "x"}-${i}`}>
              <div className="contact-top">
                <div>
                  <span className="contact-name">{w.email ?? "(no email)"}</span>
                  {" — "}
                  <span className="contact-company">
                    {w.company ?? "(no company)"}
                  </span>
                </div>
              </div>
              {w.reason && <div className="rationale">{w.reason}</div>}
            </div>
          ))}
        </>
      )}

      <h3>Qualified contacts ({contacts.length})</h3>
      {contacts.length === 0 ? (
        <p className="empty">No contacts above the score threshold.</p>
      ) : (
        contacts.map((c, i) => (
          <div className="contact" key={`${c.email ?? "x"}-${i}`}>
            <div className="contact-top">
              <div>
                <span className="contact-name">{c.name ?? "(no name)"}</span>
                {" — "}
                <span className="contact-company">
                  {c.company ?? "(no company)"}
                </span>
              </div>
              <span className="score">{c.icp_score ?? "?"}/100</span>
            </div>
            {c.email && <div className="contact-email">{c.email}</div>}
            <BreakdownGrid breakdown={c.breakdown} />
            {c.rationale && (
              <>
                <div className="rationale-label">Rationale</div>
                <div className="rationale">{c.rationale}</div>
              </>
            )}
          </div>
        ))
      )}
    </div>
  );
}
